/**
 * Convert a string to title case.  For example, 'title case' to 'Title Case'.
 * Will only affect the first letter in each word.
 *
 * @param str String to be converted.
 * @param locale Locale by which to do the case conversion. Defaults to host locale.
 * @returns Title Cased String.
 */
export function toTitleCase(str: string, locale?: string): string {
  let prevChr: string | undefined
  return str.split('').reduce((acc, chr) => {
    if (!prevChr || prevChr === ' ') {
      acc = `${acc}${chr.toLocaleUpperCase(locale)}`
    } else {
      acc = `${acc}${chr}`
    }
    prevChr = chr
    return acc
  }, '')
}

export const isCapital = (char: string) => char >= 'A' && char <= 'Z'

export const isLower = (char: string) => char >= 'a' && char <= 'z'

const isSeparator = (char: string) => /[_\-\.]/.test(char)

/**
 * Convert string to conform to best practices for analytics strings.
 * Capitalize each word, separate with spaces.
 * Preserves acronyms (consecutive capital letters) as-is.
 */
export function convertToAnalyticsString(str: string) {
  const processedString = str
    .split('')
    .reduce((result, currentChar, index) => {
      // Get characters for context
      const prevChar = result[result.length - 1]
      const prevOriginalChar = str[index - 1] ?? ''
      const nextOriginalChar = str[index + 1] ?? ''

      // Skip leading separators
      if (isSeparator(currentChar) && result.length === 0) {
        return result
      }

      // Handle separators by converting to space
      if (isSeparator(currentChar)) {
        return `${result} `
      }

      // Handle capital letters that need space insertion
      if (isCapital(currentChar) && prevChar && prevChar !== ' ') {
        const needsSpace = shouldInsertNewSeparatorBeforeCapital(prevOriginalChar, nextOriginalChar)
        return `${result}${needsSpace ? ' ' : ''}${currentChar}`
      }

      // Default case: just append the character
      return `${result}${currentChar}`
    }, '')
    .replace(/ +/g, ' ') // Collapse multiple spaces
    .trim() // Remove leading/trailing spaces

  return toTitleCase(processedString)
}

/**
 * Helps preserve acronyms from getting split apart when formatting strings.
 *
 * Spaces should be inserted in two cases:
 * 1. Previous char is NOT capital (normal camelCase: "camelCase" -> "camel Case")
 * 2. Previous char IS capital AND next char is lowercase (end of acronym: "XMLHttp" -> "XML Http")
 */
export function shouldInsertNewSeparatorBeforeCapital(prevChar: string, nextChar: string): boolean {
  const isPrevCapital = isCapital(prevChar)
  const isNextLower = isLower(nextChar)

  return !isPrevCapital || (isPrevCapital && isNextLower)
}

// Matches tags with an alphanumeric/hyphen prefix followed by a semantic version, e.g. "web-15.2.1"
const releaseTagPattern = /^[a-zA-Z0-9-]+\d+\.\d+\.\d+$/
const releaseTagMaxLength = 100

export function isValidReleaseTag(tag: string): boolean {
  return tag.length <= releaseTagMaxLength && releaseTagPattern.test(tag)
}

// Wrapper around string.slice() to avoid chopping non-BMP characters (emojis, extended CJK, etc.) in half.
export function safeSlice(text: string, start = 0, end = text.length): string {
  const startCode = text.charCodeAt(start)
  const endCode = text.charCodeAt(end - 1)
  const adjustedStart = startCode >= 0xdc00 && startCode <= 0xdfff ? start + 1 : start
  const adjustedEnd = endCode >= 0xd800 && endCode <= 0xdbff ? end - 1 : end
  return text.slice(adjustedStart, adjustedEnd)
}
